import { Component, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { QuillEditorComponent } from 'ngx-quill'
import { UserToDosComponent } from 'src/app/home/user-to-dos/user-to-dos.component'

import { ListItem, RepositoryModal, ToDo, quillLinkHandler } from '../../shared'
import { ToDosComponent } from './to-dos.component'

@Component({
    templateUrl: './to-do.component.html',
    standalone: true,
    imports: [FormsModule, WjInputModule, FaIconComponent, QuillEditorComponent],
})
export class ToDoComponent extends RepositoryModal<ToDo> implements OnInit {
    faQuestionCircle = faCircleQuestion
    userList: ListItem<string>[] = []
    meetingsList: ListItem<string>[] = []
    isUserToDosMode = false

    // When set (e.g. converting an opportunity into a to-do), this user is pre-checked instead of the current user.
    preselectedUserId?: string

    weekOfMonthList: ListItem<number>[] = [
        new ListItem(1, '1st'),
        new ListItem(2, '2nd'),
        new ListItem(3, '3rd'),
        new ListItem(4, '4th'),
        new ListItem(5, 'Last'),
    ]

    dayOfWeekList: ListItem<number>[] = [
        new ListItem(0, 'Sunday'),
        new ListItem(1, 'Monday'),
        new ListItem(2, 'Tuesday'),
        new ListItem(3, 'Wednesday'),
        new ListItem(4, 'Thursday'),
        new ListItem(5, 'Friday'),
        new ListItem(6, 'Saturday'),
    ]

    editorModules = {
        toolbar: {
            container: [
                ['bold', 'italic', 'underline'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ header: [1, 2, 3, false] }],
                ['link'],
            ],
            handlers: { link: quillLinkHandler },
        },
        clipboard: {
            matchVisual: false,
        },
    }

    ngOnInit() {
        const parentComponent = this.editOptions.initializeParams as ToDosComponent | UserToDosComponent
        this.isUserToDosMode = parentComponent instanceof UserToDosComponent || (parentComponent as any).isUserToDos === true

        if (this.isUserToDosMode) {
            // UserToDosComponent mode - no meeting-related logic
            const userToDosComponent = parentComponent as UserToDosComponent
            this.userList = [...userToDosComponent.allActiveUserList]

            if (this.isAdding) {
                const userToCheck = this.preselectedUserId ?? this.services.authentication.userId()
                this.userList.forEach(user => {
                    if (user.value === userToCheck) {
                        user.$checked = true
                    }
                })
                this.target.dueDate = new Date().addDays(7) // Default to 7 days from now
            }
        } else {
            // ToDosComponent mode - meeting-related logic
            const toDosComponent = parentComponent as ToDosComponent
            this.userList = toDosComponent.allActiveUserList.filter(listItem => toDosComponent.meeting()?.userIds.includes(listItem.value))
            this.meetingsList = toDosComponent.meetings().map(meeting => new ListItem<string>(meeting.id, meeting.name))

            if (this.isAdding) {
                const userToCheck = this.preselectedUserId ?? this.services.authentication.userId()
                this.userList.forEach(user => {
                    if (user.value === userToCheck) {
                        user.$checked = true
                    }
                })

                const currentMeetingId = toDosComponent.meetingId()
                this.meetingsList.forEach(meeting => {
                    if (meeting.value === currentMeetingId) {
                        meeting.$checked = true
                    }
                })

                this.target.dueDate = new Date().addDays(7) // Default to 7 days from now
            } else {
                this.meetingsList.forEach(meeting => {
                    if (this.target.meetingIds.includes(meeting.value)) {
                        meeting.$checked = true
                    }
                })
            }
        }
    }

    onRecurringChange() {
        if (this.target.isRecurring) {
            // Seed a sensible default pattern from the current due date when first enabled.
            if (this.target.recurrenceWeekOfMonth == null || this.target.recurrenceDayOfWeek == null) {
                const due = this.toLocalDate(this.target.dueDate)
                this.target.recurrenceDayOfWeek = due.getDay()
                this.target.recurrenceWeekOfMonth = this.weekOfMonthFor(due)
            }
        } else {
            this.target.recurrenceWeekOfMonth = undefined
            this.target.recurrenceDayOfWeek = undefined
        }
    }

    /**
     * Due dates come back from the API as an offset timestamp at midnight. Reading the weekday off
     * that directly can land on the previous day whenever the browser's offset differs from the
     * stored one, so rebuild the date from its calendar parts first.
     */
    private toLocalDate(value?: Date): Date {
        if (!value) return new Date()

        const due = new Date(value)
        return new Date(due.getFullYear(), due.getMonth(), due.getDate())
    }

    /**
     * Returns 1-4 for the first-fourth occurrence of that weekday in the month, or 5 ("Last") when
     * the date is the final occurrence. Month-end items need "Last" rather than a fixed 4th, since
     * in five-weekday months the 4th falls a week early.
     */
    private weekOfMonthFor(due: Date): number {
        const occurrence = Math.ceil(due.getDate() / 7)
        const daysInMonth = new Date(due.getFullYear(), due.getMonth() + 1, 0).getDate()
        const isLastOccurrence = due.getDate() + 7 > daysInMonth

        return isLastOccurrence ? 5 : occurrence
    }

    protected onBeforeSave(): boolean {
        if (!this.target.name) {
            this.services.notification.warning('To-Do name is required')
            return false
        }

        if (this.target.isRecurring) {
            if (this.target.recurrenceWeekOfMonth == null || this.target.recurrenceDayOfWeek == null) {
                this.services.notification.warning('Please choose the week and day for the monthly repeat.')
                return false
            }
        } else {
            this.target.recurrenceWeekOfMonth = undefined
            this.target.recurrenceDayOfWeek = undefined
        }

        if (!this.isUserToDosMode) {
            // Only set meetingIds when in ToDosComponent mode
            this.target.meetingIds = this.meetingsList.filter(meeting => meeting.$checked).map(meeting => meeting.value)
        }

        if (this.isAdding) {
            this.target.userIds = this.userList.filter(user => user.$checked).map(user => user.value)
            if (this.target.userIds.length === 0) {
                this.services.notification.warning('Please select at least one user to assign to the To-Do.')
                return false
            }

            // Send an empty Guid, since the userId is a required field, but not used when adding a new To-Do
            this.target.userId = this.services.utility.emptyGuid()
        }

        return true
    }
}
